class Node<T> {
  data: any;
  next: Node<T> | null;
  prev: Node<T> | null;
  constructor(data: any) {
    this.data = data;
    this.next = null;
    this.prev = null;
  }
}

export class DoubleLinkedList<T> {
  head: Node<T> | null;
  tail: Node<T> | null;
  size: number;
  constructor() {
    this.head = null;
    this.tail = null;
    this.size = 0;
  }

  // add a data to the end of the list (TAIL)
  append(data: T): void {
    const newNode: Node<T> = new Node(data);
    if (this.tail) {
      this.tail.next = newNode;
      newNode.prev = this.tail;
      this.tail = newNode;
    } else {
      this.head = newNode;
      this.tail = newNode;
    }
    this.size++;
  }

  // add data to the start of the list (HEAD)
  prepend(data: T): void {
    const newNode: Node<T> = new Node<T>(data);
    if (this.head) {
      this.head.prev = newNode;
      newNode.next = this.head;
      this.head = newNode;
    } else {
      this.head = newNode;
      this.tail = newNode;
    }
    this.size++;
  }

  // insert data at a specific position
  insertAt(data: T, position: number): void {
    if (position < 0 || position > this.size - 1) {
      throw Error("Position " + position + " is not in the list");
    }
    if (position === 0) {
      this.prepend(data);

      return;
    }
    if (position === this.size) {
      this.append(data);

      return;
    }
    let currentNode: Node<T> = this.head as Node<T>;
    let index: number = 0;
    while (index < position) {
      currentNode = currentNode.next as Node<T>;
      index++;
    }
    const newNode: Node<T> = new Node<T>(data);
    newNode.next = currentNode;
    newNode.prev = currentNode.prev;
    (currentNode.prev as Node<T>).next = newNode;
    currentNode.prev = newNode;
    this.size++;
  }

  // remove a node at a specific position ad return its data
  removeAt(position: number): T {
    if (position < 0 || position > this.size - 1) {
      throw Error("Position " + position + " is not in the list");
    }
    if (position === 0) {
      return this.removeHead();
    }
    if (position === this.size - 1) {
      return this.removeTail();
    }
    let current: Node<T> = this.head as Node<T>;
    let index: number = 0;
    while (index < position) {
      current = current.next as Node<T>;
      index++;
    }
    (current.prev as Node<T>).next = current.next;
    (current.next as Node<T>).prev = current.prev;
    this.size--;

    return current.data;
  }

  // return node data at a specific position
  getAt(position: number): T | null {
    if (position < 0 || position >= this.size) {
      return null;
    }
    let current = this.head;
    let index = 0;
    while (index < position) {
      current = (current as Node<T>).next;
      index++;
    }

    return (current as Node<T>).data;
  }

  // return head data
  getHead(): T | null {
    if (this.size === 0) {
      return null;
    }

    return (this.head as Node<T>).data;
  }

  updateHead(data: T): void {
    if (this.head) {
      this.head.data = data;
    }
  }

  // return tail data
  getTail(): T {
    if (this.size === 0) {
      throw Error("No tail in an empty list");
    }

    this.head = (this.tail as Node<T>).prev;
    if (this.tail && this.head) {
      this.tail.prev = this.head.next;
    }

    return (this.head as Node<T>).data;
  }

  // remove list head and return its data
  removeHead(): T {
    if (this.size === 0) {
      throw Error("No head in an empty list");
    }
    const data: T = (this.head as Node<T>).data;
    this.head = (this.head as Node<T>).next;
    if (this.head) {
      this.head.prev = null;
    } else {
      this.tail = null;
    }
    this.size--;

    return data;
  }

  // remove list tail and return its data
  removeTail(): T {
    if (this.size === 0) {
      throw Error("No tail in an empty list");
    }
    const data: T = (this.tail as Node<T>).data;
    this.tail = (this.tail as Node<T>).prev;
    if (this.tail) {
      this.tail.next = null;
    } else {
      this.head = null;
    }
    this.size--;

    return data;
  }

  moveForward(): T | null {
    if (this.head && this.head.next) {
      this.head = (this.head as Node<T>).next;

      return (this.head as Node<T>).data;
    }

    return null;
  }

  moveBackward(): T | null {
    if (this.head && this.head.prev) {
      this.head = (this.head as Node<T>).prev;

      return (this.head as Node<T>).data;
    }

    return null;
  }

  toArray(): T[] {
    const array: T[] = [];
    let current = this.head;

    // Move to the actual start of the list
    while (current && current.prev) {
      current = current.prev;
    }

    // Traverse forward from the true head
    while (current) {
      array.push(current.data);
      current = current.next;
    }

    return array;
  }

  static fromArray<T>(items: T[]): DoubleLinkedList<T> {
    const list = new DoubleLinkedList<T>();
    items.forEach((item) => list.append(item));

    return list;
  }

  hasNext(): boolean {
    return this.head !== null && this.head.next !== null;
  }

  hasBack(): boolean {
    return this.head !== null && this.head.prev !== null;
  }

  indexOf(data: T): number {
    const array: T[] = [];
    let current = this.head;

    while (current && current.prev) {
      current = current.prev;
    }

    while (current) {
      array.push(current.data);
      current = current.next;
    }

    return array.findIndex((item) => item === data);
  }

  toJSON() {
    return this.toArray();
  }
}
